// /quiero-ser-portero — landing pública para postularse como vecino/portero referido de Limpack.
// Es solo PREVIEW por ahora — el submit todavía no guarda en DB (esperando aprobación del flujo).

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import TurnstileWidget from '../components/TurnstileWidget'

const TURNOS = [
  { v: 'manana',   l: '🌅 Mañana (6:00 - 14:00)' },
  { v: 'tarde',    l: '🌇 Tarde (14:00 - 22:00)' },
  { v: 'noche',    l: '🌙 Noche (22:00 - 6:00)' },
  { v: 'full',     l: '⏰ Full-time (24h o doy vueltas)' },
  { v: 'rotativo', l: '🔄 Rotativo / variable' },
]

const FUENTES = [
  'Vi a un Limpack en mi edificio',
  'Me lo recomendó otro portero',
  'Lo vi en redes (Instagram/Facebook)',
  'Me llegó por WhatsApp',
  'Lo conocí en la calle / publicidad',
  'Otro',
]

export default function QuieroSerPortero() {
  const [form, setForm] = useState({
    nombre: '', celular: '', email: '',
    direccion: '', edificio_nombre: '',
    barrio_id: '', cant_unidades: '',
    turno: '', fuente: '', mensaje: '',
    acepta_tyc: false,
  })
  const [enviando, setEnviando] = useState(false)
  const [enviado, setEnviado] = useState(false)
  const [error, setError] = useState(null)
  const [barrios, setBarrios] = useState([])
  const [mostrarTyc, setMostrarTyc] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState(null)

  // Cargar barrios reales desde DB (anon access permitido)
  useEffect(() => {
    supabase
      .from('barrios').select('id, nombre').eq('activo', true).order('nombre')
      .then(({ data }) => setBarrios(data || []))
  }, [])

  function set(campo, valor) { setForm(f => ({ ...f, [campo]: valor })) }

  function validar() {
    if (!form.nombre.trim() || form.nombre.trim().length < 3) return 'Necesitamos tu nombre completo'
    if (!/^\+?\d{8,}$/.test(form.celular.replace(/\s/g, ''))) return 'Celular inválido (ej: 098 123 456)'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Email inválido'
    if (!form.direccion.trim()) return 'Falta la dirección del edificio donde trabajás'
    if (!form.barrio_id) return 'Elegí un barrio'
    const u = parseInt(form.cant_unidades)
    if (!u || u < 4 || u > 500) return 'Cantidad de apartamentos: poné un número entre 4 y 500'
    if (!form.acepta_tyc) return 'Tenés que aceptar los términos del programa'
    if (!turnstileToken) return 'Completá la verificación anti-bot antes de enviar'
    return null
  }

  async function submit(e) {
    e.preventDefault()
    setError(null)
    const err = validar()
    if (err) { setError(err); return }
    setEnviando(true)
    try {
      const { data, error: rpcError } = await supabase.rpc('crear_solicitud_portero', {
        p_form: {
          nombre: form.nombre.trim(),
          celular: form.celular.trim(),
          email: form.email.trim(),
          direccion: form.direccion.trim(),
          edificio_nombre: form.edificio_nombre.trim(),
          barrio_id: form.barrio_id,
          cant_unidades: form.cant_unidades,
          turno: form.turno,
          fuente: form.fuente,
          mensaje: form.mensaje.trim(),
          tyc_aceptado: form.acepta_tyc,
          tyc_version: '1.0',
        },
      })
      if (rpcError) throw rpcError
      setEnviado(true)
    } catch (e) {
      setError(e.message || 'Error al enviar. Probá de nuevo.')
    } finally {
      setEnviando(false)
    }
  }

  if (enviado) return (
    <div className="qsp-app">
      <div className="qsp-thanks">
        <div style={{ fontSize: 64 }}>🙌</div>
        <h1>¡Recibimos tu pedido!</h1>
        <p>
          Gracias <strong>{form.nombre.split(' ')[0]}</strong>, qué bueno que quieras ayudarnos a llevar Limpack a más vecinos.
        </p>
        <p>
          Nos vamos a comunicar a la brevedad por WhatsApp al <strong>{form.celular}</strong> para coordinar una visita rápida al edificio y activar tu cuenta.
        </p>
        <p style={{ fontSize: 13, color: '#64748b' }}>
          Mientras tanto, si querés conocer más sobre Limpack mirá nuestra <Link to="/catalogo">tienda</Link>.
        </p>
      </div>
      <Estilos />
    </div>
  )

  return (
    <div className="qsp-app">
      {/* Hero */}
      <div className="qsp-hero">
        <div className="qsp-logo">Lim<strong>pack</strong></div>
        <h1>Sumate como <span className="qsp-acento">PorteroLimpack</span></h1>
        <p className="qsp-subtitle">
          Ganá comisión todos los meses por cada vecino del edificio que se suscriba.
          Sin invertir nada, sin manejar plata. Vos los ayudás, nosotros le entregamos.
        </p>
        <div className="qsp-stats">
          <div><strong>$50</strong><span>/mes por vecino<br/>kit Essential</span></div>
          <div><strong>$100</strong><span>/mes por vecino<br/>kit Premium</span></div>
          <div><strong>♾️</strong><span>todos los meses<br/>mientras esté suscripto</span></div>
        </div>
      </div>

      {/* Form */}
      <form className="qsp-card" onSubmit={submit}>
        <h2>Tus datos</h2>
        <p className="qsp-help">Completá y te contactamos en máximo 48hs.</p>

        <Field label="Nombre completo *" value={form.nombre}
          onChange={v => set('nombre', v)} placeholder="Juan Pérez" />

        <Field label="Celular (con código de área) *" value={form.celular}
          onChange={v => set('celular', v)} placeholder="098 123 456" type="tel" />

        <Field label="Email *" value={form.email}
          onChange={v => set('email', v)} placeholder="juan@gmail.com" type="email" />

        <h2 style={{ marginTop: 28 }}>El edificio donde trabajás</h2>

        <Field label="Dirección completa *" value={form.direccion}
          onChange={v => set('direccion', v)} placeholder="21 de Setiembre 2424" />

        <Field label="Nombre del edificio (si tiene)" value={form.edificio_nombre}
          onChange={v => set('edificio_nombre', v)} placeholder="Edificio Pocitos Plaza" />

        <div className="qsp-row">
          <FieldSelect label="Barrio *" value={form.barrio_id}
            onChange={v => set('barrio_id', v)}
            options={barrios.map(b => ({ v: b.id, l: b.nombre }))}
            placeholder={barrios.length === 0 ? 'Cargando…' : 'Elegí…'} />

          <Field label="Cantidad de apartamentos *" value={form.cant_unidades}
            onChange={v => set('cant_unidades', v.replace(/\D/g, ''))}
            placeholder="80" type="text" inputMode="numeric"
            help="Importante para evaluar tu zona" />
        </div>

        <h2 style={{ marginTop: 28 }}>Para conocerte mejor</h2>

        <FieldSelect label="Tu turno habitual" value={form.turno}
          onChange={v => set('turno', v)} options={TURNOS.map(t => ({ v: t.v, l: t.l }))}
          placeholder="Elegí…" />

        <FieldSelect label="¿Cómo te enteraste de Limpack?" value={form.fuente}
          onChange={v => set('fuente', v)} options={FUENTES.map(f => ({ v: f, l: f }))}
          placeholder="Elegí…" />

        <div className="qsp-field">
          <label>Mensaje (opcional)</label>
          <textarea value={form.mensaje} onChange={e => set('mensaje', e.target.value)}
            rows={3} placeholder="Algo que quieras contarnos…" />
        </div>

        {/* Términos y condiciones */}
        <div className="qsp-tyc-box">
          <label className="qsp-checkbox">
            <input type="checkbox" checked={form.acepta_tyc}
              onChange={e => set('acepta_tyc', e.target.checked)} />
            <span>
              He leído y acepto los <button type="button" className="qsp-link-inline"
                onClick={() => setMostrarTyc(true)}>términos del programa</button>.
            </span>
          </label>
        </div>

        {/* Anti-bot Cloudflare Turnstile */}
        <TurnstileWidget action="postulacion-portero" onToken={setTurnstileToken} />

        {error && <div className="qsp-error">⚠️ {error}</div>}

        <button type="submit" className="qsp-btn-primary"
          disabled={enviando || !form.acepta_tyc || !turnstileToken}>
          {enviando ? 'Enviando…' : 'Quiero postularme →'}
        </button>

        <p className="qsp-disclaimer">
          🔒 Tus datos solo se usan para contactarte. No los compartimos con terceros.
        </p>
      </form>

      {/* Modal de T&C */}
      {mostrarTyc && <ModalTyc onClose={() => setMostrarTyc(false)} onAceptar={() => { set('acepta_tyc', true); setMostrarTyc(false) }} />}

      <div className="qsp-footer">
        <Link to="/catalogo" className="qsp-link">← Volver a Limpack</Link>
      </div>

      <Estilos />
    </div>
  )
}

function Field({ label, value, onChange, placeholder, type = 'text', help, inputMode }) {
  return (
    <div className="qsp-field">
      <label>{label}</label>
      <input type={type} inputMode={inputMode} value={value}
        onChange={e => onChange(e.target.value)} placeholder={placeholder} />
      {help && <div className="qsp-field-help">{help}</div>}
    </div>
  )
}

function ModalTyc({ onClose, onAceptar }) {
  return (
    <div className="qsp-modal-bg" onClick={onClose}>
      <div className="qsp-modal" onClick={e => e.stopPropagation()}>
        <button className="qsp-modal-close" onClick={onClose}>✕</button>
        <h2>Términos y Condiciones — Programa PorteroLimpack</h2>
        <p className="qsp-modal-sub">Versión 1.0 · Vigencia: abril 2026 · Montevideo, Uruguay</p>

        <ol className="qsp-tyc-list">
          <li>
            <strong>1. Partes y aceptación</strong>
            <span>El presente acuerdo se celebra entre <strong>Limpack</strong> (en adelante, “la Empresa”) y la persona física que se postula como referido (en adelante, “el Portero”). La aceptación de estos términos implica el reconocimiento expreso de las condiciones aquí establecidas y constituye un acuerdo de colaboración no laboral, sin vínculo de dependencia, conforme al artículo 1247 del Código Civil uruguayo.</span>
          </li>

          <li>
            <strong>2. Requisitos de elegibilidad</strong>
            <span>Para participar del Programa, el Portero declara bajo juramento: (a) ser mayor de 18 años; (b) desempeñarse efectivamente como portero, encargado o personal de seguridad en un edificio residencial ubicado en Montevideo; (c) contar con la autorización tácita o expresa de la administración del inmueble para promover servicios entre los residentes; (d) tener domicilio constituido en la República Oriental del Uruguay.</span>
          </li>

          <li>
            <strong>3. Naturaleza del servicio prestado</strong>
            <span>El Portero actúa como referidor independiente. No existe relación de dependencia, subordinación, exclusividad ni jornada laboral. La actividad consiste exclusivamente en recomendar el servicio Limpack a residentes del edificio donde se desempeña, mediante un enlace personal único provisto por la Empresa.</span>
          </li>

          <li>
            <strong>4. Compensación</strong>
            <span>La Empresa abonará al Portero una comisión mensual de <strong>$UY 50</strong> por cada vecino activo con suscripción a kit categoría “Essential”, y <strong>$UY 100</strong> por cada vecino activo con suscripción a kit categoría “Premium”. La comisión se devenga únicamente mientras el vecino mantenga su suscripción al día. No generan comisión: compras puntuales, productos sueltos, kits comerciales, ni recompras de no suscriptores.</span>
          </li>

          <li>
            <strong>5. Liquidación y pago</strong>
            <span>El pago se realizará mediante transferencia bancaria a cuenta de titularidad del Portero, una vez alcanzado el monto mínimo acumulado de <strong>$UY 1.000</strong>. Las comisiones del primer mes calendario completo del Portero quedarán en custodia por un plazo de treinta (30) días corridos a efectos de verificación de identidad y validación del Programa. La Empresa se reserva el derecho de retener el pago ante indicios fundados de incumplimiento de estos términos.</span>
          </li>

          <li>
            <strong>6. Protección de datos personales</strong>
            <span>Conforme a la Ley N° 18.331 de Protección de Datos Personales, la Empresa garantiza la confidencialidad de los datos de los suscriptores referidos. El Portero <strong>no tendrá acceso</strong> a información personal identificable de los vecinos suscriptos (nombre, contacto, productos consumidos, medios de pago). Solo se le proporcionará información agregada y anonimizada estrictamente necesaria para el cálculo de su compensación.</span>
          </li>

          <li>
            <strong>7. Obligaciones y prohibiciones del Portero</strong>
            <span>
              El Portero se compromete a:
              <br/>(a) Promover el servicio únicamente entre residentes del edificio donde se desempeña;
              <br/>(b) Brindar información veraz sobre el servicio Limpack;
              <br/>(c) Abstenerse de prácticas desleales o engañosas.
              <br/><br/>Queda expresamente prohibido:
              <br/>• Suscribirse a sí mismo o a terceros con el objeto de obtener compensación;
              <br/>• Difundir el enlace personal en grupos masivos, redes sociales públicas o canales no autorizados;
              <br/>• Falsificar datos relativos a su identidad o al edificio donde se desempeña;
              <br/>• Utilizar el nombre o marca Limpack para fines distintos del Programa.
            </span>
          </li>

          <li>
            <strong>8. Verificación de identidad y rol</strong>
            <span>La Empresa, previo a la activación de la cuenta y en cualquier momento durante la vigencia del Programa, podrá requerir al Portero: (a) exhibición de documento de identidad vigente; (b) registro fotográfico de su persona en el frente del edificio; (c) datos de contacto de la administración del inmueble a efectos de validación. El Portero presta consentimiento expreso para la realización de dichas verificaciones, las cuales se enmarcan en la legítima protección del Programa y de los demás participantes de buena fe.</span>
          </li>

          <li>
            <strong>9. Auditoría y procedimiento ante presuntas irregularidades</strong>
            <span>La Empresa se reserva el derecho de auditar de oficio la actividad del Portero, contemplando entre otros indicadores: relación entre vecinos referidos y unidades del edificio, coincidencia de domicilios, patrones atípicos de altas. Ante la detección de presuntas irregularidades, la Empresa notificará al Portero por medios fehacientes (email registrado y/o WhatsApp) y otorgará un plazo no menor a setenta y dos (72) horas para presentar descargos. Las comisiones legítimamente devengadas serán abonadas íntegramente; aquellas que se demuestren producto de fraude serán anuladas, sin perjuicio de las acciones civiles o penales que pudieran corresponder.</span>
          </li>

          <li>
            <strong>10. Suspensión y terminación del vínculo</strong>
            <span>El Portero podrá rescindir su participación en el Programa en cualquier momento, mediante comunicación a la Empresa por canales oficiales (email o WhatsApp registrado). La Empresa podrá suspender o dar de baja la cuenta del Portero por: (a) incumplimiento grave de los presentes términos; (b) cese de su rol en el edificio originalmente declarado, sin reasignación previa; (c) inactividad superior a seis (6) meses; (d) detección de conducta fraudulenta debidamente verificada. La baja no genera derecho a indemnización alguna, sin perjuicio del pago de las comisiones devengadas hasta el momento de la terminación.</span>
          </li>

          <li>
            <strong>11. Modificaciones a los presentes términos</strong>
            <span>La Empresa podrá modificar estos términos de manera unilateral. Toda modificación será comunicada al Portero con una antelación no menor a quince (15) días corridos a su entrada en vigencia, mediante el correo electrónico declarado. La continuidad del Portero en el Programa con posterioridad a dicho plazo implicará la aceptación tácita de las nuevas condiciones. Las comisiones devengadas con anterioridad a la modificación se rigen por los términos vigentes al momento de su generación.</span>
          </li>

          <li>
            <strong>12. Limitación de responsabilidad</strong>
            <span>La Empresa no se responsabiliza por: (a) interpretaciones erróneas que el Portero pueda transmitir a los vecinos sobre el servicio; (b) conflictos derivados de la relación laboral del Portero con la administración del edificio; (c) cancelaciones o pausas de suscripciones que impacten en la compensación esperada; (d) eventos de fuerza mayor que afecten la operación del Programa.</span>
          </li>

          <li>
            <strong>13. Jurisdicción y ley aplicable</strong>
            <span>El presente acuerdo se rige por las leyes de la República Oriental del Uruguay. Para toda controversia derivada de su interpretación o ejecución, las partes se someten a la jurisdicción de los Tribunales Ordinarios del departamento de Montevideo, renunciando expresamente a cualquier otro fuero o jurisdicción que pudiera corresponderles.</span>
          </li>

          <li>
            <strong>14. Notificaciones</strong>
            <span>Toda notificación entre las partes se considerará válidamente realizada al correo electrónico y/o número de WhatsApp declarados al momento de la postulación. Es responsabilidad del Portero mantener actualizados sus datos de contacto.</span>
          </li>
        </ol>

        <div className="qsp-tyc-cierre">
          <strong>Declaración de aceptación:</strong> Al marcar la casilla y postularme, declaro haber leído, comprendido y aceptado en todos sus términos el presente acuerdo, otorgando mi consentimiento libre, expreso e informado conforme a la normativa vigente.
        </div>

        <div className="qsp-modal-footer">
          <button className="qsp-btn-secondary" onClick={onClose}>Cerrar</button>
          <button className="qsp-btn-primary qsp-btn-modal" onClick={onAceptar}>
            ✓ Acepto los términos
          </button>
        </div>
      </div>
    </div>
  )
}

function FieldSelect({ label, value, onChange, options, placeholder }) {
  return (
    <div className="qsp-field">
      <label>{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)}>
        <option value="">{placeholder}</option>
        {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  )
}

function Estilos() {
  return (
    <style>{`
      .qsp-app {
        max-width: 480px; margin: 0 auto;
        padding: 0 16px 60px;
        background: #f8fafc; min-height: 100vh;
        font-family: system-ui, -apple-system, sans-serif;
      }
      .qsp-hero {
        background: linear-gradient(135deg, #1A3461 0%, #1a3461 100%);
        color: #fff;
        margin: 0 -16px 20px;
        padding: 28px 20px 36px;
        text-align: center;
      }
      .qsp-logo { font-size: 22px; font-weight: 400; opacity: .9; margin-bottom: 14px; }
      .qsp-logo strong { font-weight: 900; color: #00A8B5; }
      .qsp-hero h1 { font-size: 26px; font-weight: 800; margin: 0 0 10px; line-height: 1.2; }
      .qsp-acento { color: #00A8B5; }
      .qsp-subtitle { font-size: 14px; opacity: .9; line-height: 1.5; margin: 0 auto; max-width: 360px; }
      .qsp-stats {
        display: grid; grid-template-columns: 1fr 1fr 1fr;
        gap: 8px; margin-top: 24px;
      }
      .qsp-stats > div {
        background: rgba(255,255,255,.1);
        border: 1px solid rgba(255,255,255,.15);
        border-radius: 10px; padding: 12px 6px;
        display: flex; flex-direction: column; align-items: center; gap: 4px;
      }
      .qsp-stats strong { font-size: 22px; font-weight: 900; color: #00A8B5; }
      .qsp-stats span { font-size: 10px; line-height: 1.3; opacity: .85; }

      .qsp-card {
        background: #fff; border-radius: 14px;
        padding: 20px; box-shadow: 0 4px 16px rgba(15,31,61,.06);
      }
      .qsp-card h2 { font-size: 16px; color: #1a3461; margin: 0 0 6px; }
      .qsp-help { font-size: 12px; color: #64748b; margin: 0 0 16px; }

      .qsp-field { margin-bottom: 14px; }
      .qsp-field label {
        display: block; font-size: 12px; font-weight: 700;
        color: #475569; margin-bottom: 5px; text-transform: uppercase; letter-spacing: .03em;
      }
      .qsp-field input, .qsp-field select, .qsp-field textarea {
        width: 100%; box-sizing: border-box;
        padding: 12px 14px; min-height: 44px;
        border: 1px solid #d1d5db; border-radius: 10px;
        font-size: 15px; font-family: inherit;
        background: #fff;
      }
      .qsp-field textarea { min-height: 80px; resize: vertical; }
      .qsp-field input:focus, .qsp-field select:focus, .qsp-field textarea:focus {
        outline: none; border-color: #1a3461;
        box-shadow: 0 0 0 3px rgba(26,52,97,.1);
      }
      .qsp-field-help { font-size: 11px; color: #94a3b8; margin-top: 4px; }

      .qsp-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }

      .qsp-error {
        background: #fee2e2; border: 1px solid #fca5a5;
        color: #991b1b; padding: 10px 12px; border-radius: 8px;
        font-size: 13px; margin-bottom: 12px;
      }
      .qsp-btn-primary {
        width: 100%; min-height: 52px;
        background: #00A8B5; color: #1A3461;
        border: 0; border-radius: 12px;
        font-size: 16px; font-weight: 800;
        cursor: pointer; margin-top: 8px;
        box-shadow: 0 4px 14px rgba(0,196,212,.3);
      }
      .qsp-btn-primary:disabled { opacity: .6; cursor: wait; }

      .qsp-disclaimer {
        font-size: 11px; color: #94a3b8;
        text-align: center; line-height: 1.5;
        margin-top: 14px;
      }

      .qsp-footer { text-align: center; margin-top: 20px; }
      .qsp-link { color: #1a3461; font-size: 13px; text-decoration: none; }

      .qsp-thanks {
        background: #fff; border-radius: 16px;
        padding: 40px 24px; margin-top: 40px;
        text-align: center;
        box-shadow: 0 4px 16px rgba(15,31,61,.06);
      }
      .qsp-thanks h1 { color: #1a3461; font-size: 24px; margin: 12px 0 12px; }
      .qsp-thanks p { color: #475569; line-height: 1.6; font-size: 14px; }

      /* T&C checkbox + modal */
      .qsp-tyc-box {
        margin-top: 18px; padding: 14px;
        background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px;
      }
      .qsp-checkbox {
        display: flex; gap: 10px; align-items: flex-start;
        cursor: pointer; font-size: 13px; color: #334155; line-height: 1.5;
      }
      .qsp-checkbox input[type=checkbox] {
        width: 20px; height: 20px;
        margin-top: 1px; flex-shrink: 0;
        accent-color: #00A8B5;
      }
      .qsp-link-inline {
        background: none; border: 0; padding: 0;
        color: #1a3461; font-weight: 700; text-decoration: underline;
        cursor: pointer; font-size: 13px; font-family: inherit;
      }
      .qsp-btn-secondary {
        padding: 12px 20px; min-height: 44px;
        background: #fff; color: #475569;
        border: 1px solid #d1d5db; border-radius: 10px;
        font-size: 14px; font-weight: 600;
        cursor: pointer;
      }

      .qsp-modal-bg {
        position: fixed; inset: 0; z-index: 1000;
        background: rgba(15,31,61,.55); backdrop-filter: blur(4px);
        display: flex; align-items: center; justify-content: center;
        padding: 16px;
      }
      .qsp-modal {
        background: #fff; border-radius: 16px;
        max-width: 520px; width: 100%; max-height: 90vh;
        overflow-y: auto;
        padding: 24px; position: relative;
        box-shadow: 0 16px 48px rgba(15,31,61,.3);
      }
      .qsp-modal-close {
        position: absolute; top: 14px; right: 14px;
        width: 32px; height: 32px; border-radius: 50%;
        background: #f1f5f9; border: 0; cursor: pointer;
        font-size: 14px; color: #475569;
      }
      .qsp-modal h2 { color: #1a3461; font-size: 20px; margin: 0 0 6px; padding-right: 30px; }
      .qsp-modal-sub { font-size: 13px; color: #64748b; margin: 0 0 18px; }

      .qsp-tyc-list { padding-left: 0; margin: 0; list-style: none; }
      .qsp-tyc-list li {
        margin-bottom: 16px; font-size: 13px; line-height: 1.65; color: #334155;
        text-align: justify;
      }
      .qsp-tyc-list li strong {
        display: block; color: #1a3461; font-size: 13px; margin-bottom: 5px;
        text-transform: none; letter-spacing: 0;
      }
      .qsp-tyc-cierre {
        background: #f1f5f9; border-left: 3px solid #1a3461;
        padding: 12px 14px; border-radius: 0 8px 8px 0;
        font-size: 12px; line-height: 1.6; color: #334155;
        margin: 18px 0; text-align: justify;
      }
      .qsp-tyc-cierre strong { color: #1a3461; }

      .qsp-modal-footer {
        display: flex; gap: 10px; margin-top: 20px;
        padding-top: 18px; border-top: 1px solid #e2e8f0;
        position: sticky; bottom: -24px; background: #fff;
      }
      .qsp-modal-footer button { flex: 1; }
      .qsp-btn-modal { margin: 0; min-height: 44px; }
    `}</style>
  )
}
